import { Outlet, createRootRoute, Link, useRouterState } from '@tanstack/react-router'
import { QueryClientProvider, useQueryClient } from '@tanstack/react-query'
import { Toaster } from 'sonner'
import Footer from '@/components/Footer'
import React, { useState, useEffect, useRef } from 'react'
import {
  ArrowRight,
  BarChart3,
  BookOpenCheck,
  Bot,
  Building2,
  CalendarDays,
  ChevronDown,
  CircleHelp,
  FileBarChart,
  GraduationCap,
  HeartHandshake,
  LibraryBig,
  Menu,
  MessageCircleMore,
  NotebookTabs,
  PlayCircle,
  School,
  ShieldCheck,
  Sparkles,
  UsersRound,
  X,
} from 'lucide-react'
import { schoolService } from '@/services/schoolService'
import { SchoolLandingPage } from '@/components/school/SchoolLandingPage'
import { useQuery, useMutation as useRQMutation } from '@tanstack/react-query'
import UserProfileMenu from '@/components/UserProfileMenu'
import MessageIcon from '@/components/MessageIcon'
import { SubdomainDebugger } from '@/components/SubdomainDebugger'
import { SEOProvider } from '@/contexts/SEOContext'
import { queryClient } from '@/lib/queryClient'
import { useTranslation } from 'react-i18next'
import api from '@/lib/axios'
import { useUserStore, initializeUserStore } from '@/store/userStore'

const PUBLIC_ROUTE_PREFIXES = [
  '/',
  '/about',
  '/ai-demo',
  '/comparison',
  '/contact',
  '/contactUs',
  '/demo',
  '/feature',
  '/features',
  '/forgot-password',
  '/login',
  '/new-password',
  '/pricing',
  '/privacy-policy',
  '/register',
  '/school',
  '/schools',
  '/tutoriales',
  '/why-jeridschool',
]

const isPublicRoute = (pathname: string) =>
  pathname === '/' ||
  PUBLIC_ROUTE_PREFIXES.slice(1).some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  )

export const Route = createRootRoute({
  component: RootComponent,
  beforeLoad: async ({ location }) => {
    // Public pages must render even when the account API is unavailable. The
    // session check still runs in the background so signed-in navigation can
    // update, while protected routes continue to wait for verification.
    if (isPublicRoute(location.pathname)) {
      void initializeUserStore()
    } else {
      await initializeUserStore()
    }
    
    const schoolPathMatch = location.pathname.match(/^\/school\/([^\/]+)$/)

    if (schoolPathMatch && !schoolService.isSubdomainUrl()) {
      const schoolName = schoolPathMatch[1]
      if (typeof window !== 'undefined') {
        schoolService.redirectToSubdomain(schoolName)
      }
    }

    return {}
  },
  notFoundComponent: () => {
    const NotFound = React.lazy(() => import('@/components/notfound'))
    return (
      <React.Suspense fallback={<div>Loading...</div>}>
        <NotFound />
      </React.Suspense>
    )
  },
})

function RootComponent() {
  const { t, i18n } = useTranslation()
  const pathname = useRouterState({ select: (state) => state.location.pathname })
  const hasTrackedInitialPageView = useRef(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [activeMegaMenu, setActiveMegaMenu] = useState<string | null>(null)
  const megaMenuCloseTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)
  const queryClientHook = useQueryClient()
  
  // Get user data from userStore (fetched from API)
  const user = useUserStore((state) => state.user)
  const isAuthenticated = useUserStore((state) => state.isAuthenticated)
  const isLoggedIn = isAuthenticated && !!user

  useEffect(() => {
    if (!hasTrackedInitialPageView.current) {
      hasTrackedInitialPageView.current = true
      return
    }

    ;(window as Window & { fbq?: (...args: unknown[]) => void }).fbq?.(
      'track',
      'PageView'
    )
  }, [pathname])

  useEffect(() => {
    const handleUserLogin = () => {
      // User login event - state will update automatically via userStore
    }
    const handleUserLogout = () => {
      // User logout event - state will update automatically via userStore
    }
    window.addEventListener('user-login', handleUserLogin)
    window.addEventListener('user-logout', handleUserLogout)
    return () => {
      window.removeEventListener('user-login', handleUserLogin)
      window.removeEventListener('user-logout', handleUserLogout)
    }
  }, [])

  // Cleanup dropdown timeout on unmount
  useEffect(() => {
    return () => {
      if (megaMenuCloseTimeout.current) {
        clearTimeout(megaMenuCloseTimeout.current)
      }
    }
  }, [])

  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setActiveMegaMenu(null)
    }
    window.addEventListener('keydown', closeOnEscape)
    return () => window.removeEventListener('keydown', closeOnEscape)
  }, [])

  // Detect browser language and map to allowed values
  const getBrowserLanguageCode = (): 'EN' | 'FR' => {
    const navLang = (navigator.language || navigator.languages?.[0] || 'en').toLowerCase()
    return navLang.startsWith('fr') ? 'FR' : 'EN'
  }

  const applyI18nFromCode = (code: 'EN' | 'FR') => {
    const lng = code === 'FR' ? 'fr' : 'en'
    if ((i18n.language || '').slice(0, 2) !== lng) {
      i18n.changeLanguage(lng)
    }
  }

  // Fetch user language using TanStack Query
  const userId = user?.id
  const userLanguageQuery = useQuery<string | undefined>({
    queryKey: ['userLanguage', userId],
    queryFn: async () => {
      const { data } = await api.get('/user-controller/language')
  // console.logwe get the language', data)
      if (data?.language === "XX") {
        updateLanguageMutation.mutate(getBrowserLanguageCode())
    // console.logwe update the language', getBrowserLanguageCode())
      }
      return String(data?.language || 'XX').toUpperCase()
    },
    enabled: isLoggedIn && !!userId,
    staleTime: 0,
    gcTime: 0,
    retry: 1,
  })

  // Mutation to update user language
  const updateLanguageMutation = useRQMutation({
    mutationFn: async (code: 'EN' | 'FR') => {
      const res = await api.patch('/user-controller/updatelanguage', { language: code })
      return String(res.data?.language || code).toUpperCase() as 'EN' | 'FR'
    },
    onSuccess: (newCode) => {
      // Update cache and i18n immediately
      const currentUserId = user?.id
      queryClientHook.setQueryData(
        ['userLanguage', currentUserId],
        newCode
      )
      applyI18nFromCode(newCode)
    },
  })

  // React to fetched language
  useEffect(() => {
    if (!isLoggedIn) return
    const backendCode = userLanguageQuery.data
    if (!backendCode) return
    if (backendCode === 'XX') {
      const detected = getBrowserLanguageCode()
      applyI18nFromCode(detected)
      // Persist detected to backend
      updateLanguageMutation.mutate(detected)
    } else if (backendCode === 'EN' || backendCode === 'FR') {
      applyI18nFromCode(backendCode)
    }
  }, [isLoggedIn, userLanguageQuery.data])

  const handleLogout = async () => {
    try {
      // Use userStore logout which handles backend cookie clearing
      await useUserStore.getState().logout()
      
      // Navigate to login page - use window.location to force full reload and clear all state
      window.location.replace('/login')
    } catch (error) {
      // console.error('Logout error:', error)
      // Even if logout fails, navigate to login
      window.location.replace('/login')
    }
  }

  const userRole = user?.role || null

  const getDashboardPath = () => {
    if (!userRole) return '/'

    switch (userRole) {
      case 'Admin':
        return '/admin'
      case 'SuperAdmin':
        return '/super_admin'
      case 'Jerid_School_Admin':
        return '/jerid_school_admin'
      case 'Teacher':
        return '/teacher'
      case 'Parent':
        return '/parent'
      case 'Student':
        return '/student'
      case 'B2C_Client':
        return '/b2c_student'
      default:
        return '/'
    }
  }

  const isSchoolRoute = window.location.pathname.startsWith('/school/')
  const isSubdomain = schoolService.isSubdomainUrl()
  const isWwwSubdomain =
    typeof window !== 'undefined' && window.location.hostname.startsWith('www.')

  if (isSubdomain && !isWwwSubdomain) {
    return <SubdomainSchoolComponent />
  }

  if (isSchoolRoute) {
    return (
      <QueryClientProvider client={queryClient}>
        <Outlet />
        <Toaster position="top-right" richColors />
        <FloatingWhatsAppButton />
      </QueryClientProvider>
    )
  }

  if (pathname.startsWith('/jerid_school_admin')) {
    return (
      <QueryClientProvider client={queryClient}>
        <SEOProvider>
          <Outlet />
          <Toaster position="top-right" richColors />
        </SEOProvider>
      </QueryClientProvider>
    )
  }

  const NavigationLinks = () => {
    return (
      <>
        <Link
          to={isLoggedIn ? getDashboardPath() : '/'}
          className="rounded-full px-3 py-2 text-sm font-bold text-[#243766] transition hover:bg-blue-50 hover:text-[#3156bd]"
        >
          {t('routes.root.navigation.home')}
        </Link>

        {isLoggedIn && (
          <Link
            to={getDashboardPath()}
            className="rounded-full px-3 py-2 text-sm font-bold text-[#243766] transition hover:bg-blue-50 hover:text-[#3156bd]"
          >
            {t('routes.root.navigation.dashboard')}
          </Link>
        )}

        <Link
          to="/features"
          className="rounded-full px-3 py-2 text-sm font-bold text-[#243766] transition hover:bg-blue-50 hover:text-[#3156bd]"
        >
          {t('routes.root.navigation.features')}
        </Link>

        <Link
          to="/pricing"
          className="rounded-full px-3 py-2 text-sm font-bold text-[#243766] transition hover:bg-blue-50 hover:text-[#3156bd]"
        >
          {t('routes.root.navigation.pricing')}
        </Link>

        {/* Features Dropdown */}
        {/* <div 
          className="relative"
          onMouseEnter={() => {
            if (dropdownTimeout) {
              clearTimeout(dropdownTimeout)
              setDropdownTimeout(null)
            }
            setIsFeaturesDropdownOpen(true)
          }}
          onMouseLeave={() => {
            const timeout = setTimeout(() => {
              setIsFeaturesDropdownOpen(false)
            }, 150)
            setDropdownTimeout(timeout)
          }}
        >
          <Link
            to="/features"
            className="text-gray-800 hover:text-blue-600 font-medium flex items-center gap-1 transition-colors"
          >
            {t('routes.root.navigation.features')}
            <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isFeaturesDropdownOpen ? 'rotate-180' : ''}`} />
          </Link>
          
          {isFeaturesDropdownOpen && (
            <div 
              className="absolute top-full left-0 sm:left-1/2 sm:-translate-x-1/2 mt-2 sm:mt-4 w-screen sm:w-[90vw] md:w-[96vw] max-w-6xl bg-white rounded-lg sm:rounded-2xl md:rounded-3xl shadow-2xl border border-gray-200/50 z-50 overflow-hidden"
              style={{
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.15)'
              }}
              onMouseEnter={() => {
                if (dropdownTimeout) {
                  clearTimeout(dropdownTimeout)
                  setDropdownTimeout(null)
                }
                setIsFeaturesDropdownOpen(true)
              }}
              onMouseLeave={() => {
                const timeout = setTimeout(() => {
                  setIsFeaturesDropdownOpen(false)
                }, 150)
                setDropdownTimeout(timeout)
              }}
            >
              <div className="px-3 sm:px-4 md:px-6 pt-3 sm:pt-4 md:pt-6 pb-2 sm:pb-3 md:pb-4 border-b border-gray-200 bg-gradient-to-r from-blue-50/50 to-indigo-50/50">
                <div className="flex flex-col gap-2 sm:gap-3 mb-2 sm:mb-3 md:mb-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base sm:text-lg md:text-xl font-bold text-gray-900 mb-0.5 sm:mb-1 truncate">
                        {t('routes.root.navigation.features')}
                      </h3>
                      <p className="text-[10px] sm:text-xs md:text-sm text-gray-600 truncate">
                        {t('routes.root.featuresDropdown.description')}
                      </p>
                    </div>
                    <Link
                      to="/features"
                      className="text-[10px] sm:text-xs md:text-sm font-medium hover:opacity-80 transition-opacity ml-2 flex-shrink-0"
                      style={{ color: '#525FE1' }}
                      onClick={(e) => {
                        e.stopPropagation()
                        setIsFeaturesDropdownOpen(false)
                      }}
                    >
                      {t('routes.root.featuresDropdown.allLink')} →
                    </Link>
                  </div>
                
                <div className="flex gap-1.5 sm:gap-2 overflow-x-auto -mx-3 sm:-mx-4 md:-mx-6 px-3 sm:px-4 md:px-6 pb-1">
                  {[
                    { id: 'features', label: t('routes.root.featuresDropdown.tabs.features.label'), shortLabel: t('routes.root.featuresDropdown.tabs.features.shortLabel'), icon: BookOpen },
                    { id: 'why-better', label: t('routes.root.featuresDropdown.tabs.whyBetter.label'), shortLabel: t('routes.root.featuresDropdown.tabs.whyBetter.shortLabel'), icon: TrendingUp },
                    { id: 'comparison', label: t('routes.root.featuresDropdown.tabs.comparison.label'), shortLabel: t('routes.root.featuresDropdown.tabs.comparison.shortLabel'), icon: CheckCircle2 },
                  ].map((tab) => {
                    const isActive = featuresDropdownTab === tab.id
                    const mainColor = '#525FE1'
                    const hoverBgColor = '#e0e7ff'
                    
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setFeaturesDropdownTab(tab.id as any)}
                        className={`
                          group flex items-center justify-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 md:px-4 py-1.5 sm:py-2 md:py-2.5 rounded-md sm:rounded-lg text-[10px] sm:text-xs md:text-sm font-medium whitespace-nowrap flex-shrink-0
                          transition-all duration-200 ease-in-out
                          ${
                            isActive
                              ? 'text-white shadow-md'
                              : 'bg-white text-gray-700 border border-gray-200 hover:shadow-sm'
                          }
                        `}
                        style={isActive 
                          ? { backgroundColor: mainColor } 
                          : {}
                        }
                        onMouseEnter={(e) => {
                          if (!isActive) {
                            e.currentTarget.style.backgroundColor = hoverBgColor
                            e.currentTarget.style.borderColor = mainColor
                            e.currentTarget.style.color = mainColor
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isActive) {
                            e.currentTarget.style.backgroundColor = 'white'
                            e.currentTarget.style.borderColor = '#e5e7eb'
                            e.currentTarget.style.color = '#374151'
                          }
                        }}
                      >
                        <tab.icon className="w-3 h-3 sm:w-3.5 sm:h-3.5 md:w-4 md:h-4 flex-shrink-0" />
                        <span className="hidden sm:inline">{tab.label}</span>
                        <span className="sm:hidden">{tab.shortLabel}</span>
                      </button>
                    )
                  })}
                </div>
                </div>
              </div>

              <div className="p-2.5 sm:p-3 md:p-4 lg:p-6 max-h-[calc(100vh-200px)] sm:max-h-[70vh] md:max-h-[600px] overflow-y-auto">
                {featuresDropdownTab === 'features' && (
                  <>
                    <div className="flex gap-1 sm:gap-1.5 sm:gap-2 flex-wrap mb-3 sm:mb-4 md:mb-6 overflow-x-auto -mx-2.5 sm:-mx-3 md:-mx-4 lg:-mx-6 px-2.5 sm:px-3 md:px-4 lg:px-6 pb-1">
                      {[
                        { id: 'all', label: t('routes.root.featuresDropdown.roleFilters.all.label'), shortLabel: t('routes.root.featuresDropdown.roleFilters.all.shortLabel'), icon: null },
                        { id: 'admin', label: t('routes.root.featuresDropdown.roleFilters.admin'), shortLabel: t('routes.root.featuresDropdown.roleFilters.admin'), icon: Shield },
                        { id: 'teacher', label: t('routes.root.featuresDropdown.roleFilters.teacher'), shortLabel: t('routes.root.featuresDropdown.roleFilters.teacher'), icon: BookOpen },
                        { id: 'student', label: t('routes.root.featuresDropdown.roleFilters.student'), shortLabel: t('routes.root.featuresDropdown.roleFilters.student'), icon: GraduationCap },
                        { id: 'parent', label: t('routes.root.featuresDropdown.roleFilters.parent'), shortLabel: t('routes.root.featuresDropdown.roleFilters.parent'), icon: Heart }
                      ].map((filter) => {
                        const isActive = selectedRoleFilter === filter.id
                        const mainColor = '#525FE1'
                        const hoverBgColor = '#e0e7ff'
                        const bgColor = filter.id === 'all' 
                          ? '#525FE1' 
                          : mainColor
                        
                        return (
                          <button
                            key={filter.id}
                            onClick={() => setSelectedRoleFilter(filter.id as any)}
                            className={`
                              group flex items-center gap-1 sm:gap-1.5 sm:gap-2 px-2 sm:px-2.5 md:px-3 py-1 sm:py-1.5 rounded-md sm:rounded-lg text-[9px] sm:text-[10px] md:text-xs font-medium whitespace-nowrap flex-shrink-0
                              transition-all duration-200 ease-in-out
                              ${
                                isActive
                                  ? 'text-white shadow-sm'
                                  : 'bg-gray-100 text-gray-700 hover:shadow-sm'
                              }
                            `}
                            style={isActive 
                              ? { backgroundColor: bgColor } 
                              : {}
                            }
                            onMouseEnter={(e) => {
                              if (!isActive) {
                                e.currentTarget.style.backgroundColor = hoverBgColor
                                e.currentTarget.style.borderColor = mainColor
                                e.currentTarget.style.color = mainColor
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!isActive) {
                                e.currentTarget.style.backgroundColor = '#f3f4f6'
                                e.currentTarget.style.borderColor = 'transparent'
                                e.currentTarget.style.color = '#374151'
                              }
                            }}
                          >
                            {filter.icon && (
                              <filter.icon className="w-2 h-2 sm:w-2.5 sm:h-2.5 md:w-3 md:h-3 flex-shrink-0" />
                            )}
                            <span className="hidden sm:inline">{filter.label}</span>
                            <span className="sm:hidden">{filter.shortLabel || filter.label}</span>
                          </button>
                        )
                      })}
                    </div>

                    {selectedRoleFilter === 'all' && (
                      <div className="mb-3 sm:mb-4 md:mb-6 p-2.5 sm:p-3 md:p-4 lg:p-5 rounded-md sm:rounded-lg md:rounded-xl bg-gradient-to-r from-blue-50/80 to-indigo-50/80 border border-blue-100">
                        <div className="flex items-start gap-2 sm:gap-3">
                          <div className="w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 rounded-md sm:rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#525FE1' }}>
                            <CheckCircle2 className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5 text-white" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="text-xs sm:text-sm md:text-base font-bold text-gray-900 mb-1 sm:mb-1.5 md:mb-2 line-clamp-2">
                              {t('routes.root.featuresDropdown.intro.title')}
                            </h4>
                            <p className="text-[10px] sm:text-xs md:text-sm text-gray-700 leading-relaxed mb-2 sm:mb-3 md:mb-4 line-clamp-3 sm:line-clamp-none">
                              {t('routes.root.featuresDropdown.intro.description')}
                            </p>
                            <Link
                              to="/features"
                              className="inline-flex items-center gap-1 sm:gap-1.5 sm:gap-2 px-2.5 sm:px-3 md:px-4 py-1 sm:py-1.5 md:py-2 rounded-md sm:rounded-lg text-[10px] sm:text-xs md:text-sm font-medium transition-colors hover:opacity-90"
                              style={{ backgroundColor: '#525FE1', color: 'white' }}
                              onClick={(e) => {
                                e.stopPropagation()
                                setIsFeaturesDropdownOpen(false)
                              }}
                            >
                              {t('routes.root.featuresDropdown.intro.seeAll')}
                              <ChevronDown className="w-2.5 h-2.5 sm:w-3 sm:h-3 md:w-4 md:h-4 rotate-[-90deg]" />
                            </Link>
                          </div>
                        </div>
                      </div>
                    )}

                    {selectedRoleFilter !== 'all' && (
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-1.5 sm:gap-2 md:gap-3">
                        {[
                          // Admin Features
                          { role: 'admin', icon: Users, label: 'User Management', href: '/feature/admin' },
                          { role: 'admin', icon: Calendar, label: 'Timetable', href: '/feature/admin' },
                          { role: 'admin', icon: BarChart3, label: 'Analytics', href: '/feature/admin' },
                          { role: 'admin', icon: Settings, label: 'System Config', href: '/feature/admin' },
                          { role: 'admin', icon: Shield, label: 'Security', href: '/feature/admin' },
                          
                          // Teacher Features
                          { role: 'teacher', icon: BookOpen, label: 'Lesson Planning', href: '/feature/teacher' },
                          { role: 'teacher', icon: Users, label: 'Student Mgmt', href: '/feature/teacher' },
                          { role: 'teacher', icon: Calendar, label: 'Attendance', href: '/feature/teacher' },
                          { role: 'teacher', icon: FileText, label: 'Assessments', href: '/feature/teacher' },
                          { role: 'teacher', icon: Award, label: 'Grading', href: '/feature/teacher' },
                          
                          // Student Features
                          { role: 'student', icon: BookOpen, label: 'Learning', href: '/feature/student' },
                          { role: 'student', icon: Calendar, label: 'Schedule', href: '/feature/student' },
                          { role: 'student', icon: Award, label: 'Achievements', href: '/feature/student' },
                          { role: 'student', icon: MessageSquare, label: 'Communication', href: '/feature/student' },
                          { role: 'student', icon: Users, label: 'Collaboration', href: '/feature/student' },
                          
                          // Parent Features
                          { role: 'parent', icon: Eye, label: 'Monitoring', href: '/feature/parent' },
                          { role: 'parent', icon: MessageSquare, label: 'Communication', href: '/feature/parent' },
                          { role: 'parent', icon: Calendar, label: 'Schedule', href: '/feature/parent' },
                          { role: 'parent', icon: Award, label: 'Progress', href: '/feature/parent' }
                        ]
                          .filter(feature => feature.role === selectedRoleFilter)
                          .map((feature, index) => (
                            <Link
                              key={`${feature.role}-${feature.label}-${index}`}
                              to={feature.href}
                              className="group relative"
                            >
                              <div 
                                className="relative p-2.5 sm:p-3 md:p-4 rounded-lg sm:rounded-xl bg-white border border-gray-200 transition-all duration-200 ease-in-out group-hover:shadow-lg group-hover:-translate-y-1 h-full flex flex-col items-center text-center"
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.borderColor = '#525FE1'
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.borderColor = '#e5e7eb'
                                }}
                              >
                                <div 
                                  className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 rounded-lg flex items-center justify-center mb-2 sm:mb-3 shadow-sm group-hover:shadow-md group-hover:scale-110 transition-all duration-200 ease-in-out"
                                  style={{ backgroundColor: '#525FE1' }}
                                >
                                  <feature.icon className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-white" />
                                </div>
                                
                                <span className="text-[10px] sm:text-xs font-semibold text-gray-700 group-hover:text-gray-900 transition-colors line-clamp-2">
                                  {feature.label}
                                </span>
                              </div>
                            </Link>
                          ))}
                      </div>
                    )}
                  </>
                )}

                {featuresDropdownTab === 'why-better' && (
                  <div className="space-y-4 sm:space-y-6">
                    <div>
                      <h4 className="text-base sm:text-lg font-bold text-gray-900 mb-2 sm:mb-3">{t('routes.root.featuresDropdown.whyBetter.title')}</h4>
                      <p className="text-xs sm:text-sm text-gray-600 mb-3 sm:mb-4">
                        {t('routes.root.featuresDropdown.whyBetter.description')}
                      </p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                      <div className="p-3 sm:p-4 rounded-lg sm:rounded-xl border border-gray-200 bg-blue-50/50">
                        <h5 className="text-sm sm:text-base font-bold text-gray-900 mb-1.5 sm:mb-2">{t('routes.root.featuresDropdown.whyBetter.turboLearn.title')}</h5>
                        <p className="text-xs sm:text-sm text-gray-600">
                          {t('routes.root.featuresDropdown.whyBetter.turboLearn.description')}
                        </p>
                      </div>
                      <div className="p-3 sm:p-4 rounded-lg sm:rounded-xl border border-gray-200 bg-purple-50/50">
                        <h5 className="text-sm sm:text-base font-bold text-gray-900 mb-1.5 sm:mb-2">{t('routes.root.featuresDropdown.whyBetter.timeMaster.title')}</h5>
                        <p className="text-xs sm:text-sm text-gray-600">
                          {t('routes.root.featuresDropdown.whyBetter.timeMaster.description')}
                        </p>
                      </div>
                    </div>
                    <Link
                      to="/why-jeridschool"
                      className="inline-flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors hover:opacity-90"
                      style={{ backgroundColor: '#525FE1', color: 'white' }}
                    >
                      {t('routes.root.featuresDropdown.whyBetter.learnMore')}
                      <ChevronDown className="w-3 h-3 sm:w-4 sm:h-4 rotate-[-90deg]" />
                    </Link>
                  </div>
                )}

                {featuresDropdownTab === 'comparison' && (
                  <div className="space-y-4 sm:space-y-6">
                    <div>
                      <h4 className="text-base sm:text-lg font-bold text-gray-900 mb-2 sm:mb-3">{t('routes.root.featuresDropdown.comparison.title')}</h4>
                      <p className="text-xs sm:text-sm text-gray-600 mb-3 sm:mb-4">
                        {t('routes.root.featuresDropdown.comparison.description')}
                      </p>
                    </div>
                    <div className="overflow-x-auto -mx-3 sm:-mx-4 md:-mx-6 px-3 sm:px-4 md:px-6">
                      <table className="w-full text-xs sm:text-sm min-w-[500px]">
                        <thead>
                          <tr className="border-b border-gray-200">
                            <th className="text-left py-2 sm:py-3 px-2 sm:px-4 font-semibold text-gray-900">{t('routes.root.featuresDropdown.comparison.table.headers.feature')}</th>
                            <th className="text-center py-2 sm:py-3 px-2 sm:px-4 font-semibold text-gray-900">{t('routes.root.featuresDropdown.comparison.table.headers.without')}</th>
                            <th className="text-center py-2 sm:py-3 px-2 sm:px-4 font-semibold" style={{ color: '#525FE1' }}>{t('routes.root.featuresDropdown.comparison.table.headers.with')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr className="border-b border-gray-100">
                            <td className="py-2 sm:py-3 px-2 sm:px-4 font-medium text-gray-900">{t('routes.root.featuresDropdown.comparison.table.rows.timetable.feature')}</td>
                            <td className="py-2 sm:py-3 px-2 sm:px-4 text-center text-gray-600 text-xs">{t('routes.root.featuresDropdown.comparison.table.rows.timetable.without')}</td>
                            <td className="py-2 sm:py-3 px-2 sm:px-4 text-center font-medium text-xs" style={{ color: '#525FE1' }}>{t('routes.root.featuresDropdown.comparison.table.rows.timetable.with')}</td>
                          </tr>
                          <tr className="border-b border-gray-100">
                            <td className="py-2 sm:py-3 px-2 sm:px-4 font-medium text-gray-900">{t('routes.root.featuresDropdown.comparison.table.rows.engagement.feature')}</td>
                            <td className="py-2 sm:py-3 px-2 sm:px-4 text-center text-gray-600 text-xs">{t('routes.root.featuresDropdown.comparison.table.rows.engagement.without')}</td>
                            <td className="py-2 sm:py-3 px-2 sm:px-4 text-center font-medium text-xs" style={{ color: '#525FE1' }}>{t('routes.root.featuresDropdown.comparison.table.rows.engagement.with')}</td>
                          </tr>
                          <tr className="border-b border-gray-100">
                            <td className="py-2 sm:py-3 px-2 sm:px-4 font-medium text-gray-900">{t('routes.root.featuresDropdown.comparison.table.rows.communication.feature')}</td>
                            <td className="py-2 sm:py-3 px-2 sm:px-4 text-center text-gray-600 text-xs">{t('routes.root.featuresDropdown.comparison.table.rows.communication.without')}</td>
                            <td className="py-2 sm:py-3 px-2 sm:px-4 text-center font-medium text-xs" style={{ color: '#525FE1' }}>{t('routes.root.featuresDropdown.comparison.table.rows.communication.with')}</td>
                          </tr>
                          <tr className="border-b border-gray-100">
                            <td className="py-2 sm:py-3 px-2 sm:px-4 font-medium text-gray-900">{t('routes.root.featuresDropdown.comparison.table.rows.adminWork.feature')}</td>
                            <td className="py-2 sm:py-3 px-2 sm:px-4 text-center text-gray-600 text-xs">{t('routes.root.featuresDropdown.comparison.table.rows.adminWork.without')}</td>
                            <td className="py-2 sm:py-3 px-2 sm:px-4 text-center font-medium text-xs" style={{ color: '#525FE1' }}>{t('routes.root.featuresDropdown.comparison.table.rows.adminWork.with')}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <Link
                      to="/comparison"
                      className="inline-flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors hover:opacity-90"
                      style={{ backgroundColor: '#525FE1', color: 'white' }}
                    >
                      {t('routes.root.featuresDropdown.comparison.seeFull')}
                      <ChevronDown className="w-3 h-3 sm:w-4 sm:h-4 rotate-[-90deg]" />
                    </Link>
                  </div>
                )}

                
              </div>
            </div>
          )}
        </div> */}

        <Link
          to="/about"
          className="rounded-full px-3 py-2 text-sm font-bold text-[#243766] transition hover:bg-blue-50 hover:text-[#3156bd]"
        >
          {t('routes.root.navigation.about')}
        </Link>
      </>
    )
  }

  // Keep the compact legacy link set available while the richer public menu
  // below becomes the primary navigation experience.
  void NavigationLinks

  const publicNavGroups = [
    {
      id: 'platform',
      label: t('routes.root.publicNav.platform.label'),
      description: t('routes.root.publicNav.platform.description'),
      items: [
        { icon: School, title: t('routes.root.publicNav.platform.schoolManagement'), description: t('routes.root.publicNav.platform.schoolManagementDescription'), href: '/features' },
        { icon: CalendarDays, title: t('routes.root.publicNav.platform.timetable'), description: t('routes.root.publicNav.platform.timetableDescription'), href: '/why-jeridschool' },
        { icon: FileBarChart, title: t('routes.root.publicNav.platform.exams'), description: t('routes.root.publicNav.platform.examsDescription'), href: '/comparison' },
        { icon: MessageCircleMore, title: t('routes.root.publicNav.platform.communication'), description: t('routes.root.publicNav.platform.communicationDescription'), href: '/features' },
        { icon: Building2, title: t('routes.root.publicNav.platform.institutions'), description: t('routes.root.publicNav.platform.institutionsDescription'), href: '/schools' },
        { icon: ShieldCheck, title: t('routes.root.publicNav.platform.records'), description: t('routes.root.publicNav.platform.recordsDescription'), href: '/feature/admin' },
      ],
      featured: {
        eyebrow: t('routes.root.publicNav.platform.featuredEyebrow'),
        title: t('routes.root.publicNav.platform.featuredTitle'),
        description: t('routes.root.publicNav.platform.featuredDescription'),
        href: '/demo',
      },
    },
    {
      id: 'learning',
      label: t('routes.root.publicNav.learning.label'),
      description: t('routes.root.publicNav.learning.description'),
      items: [
        { icon: Bot, title: t('routes.root.publicNav.learning.jeridoo'), description: t('routes.root.publicNav.learning.jeridooDescription'), href: '/ai-demo?tab=chat' },
        { icon: LibraryBig, title: t('routes.root.publicNav.learning.flashcards'), description: t('routes.root.publicNav.learning.flashcardsDescription'), href: '/ai-demo?tab=demo' },
        { icon: BookOpenCheck, title: t('routes.root.publicNav.learning.quizzes'), description: t('routes.root.publicNav.learning.quizzesDescription'), href: '/ai-demo?tab=demo' },
        { icon: NotebookTabs, title: t('routes.root.publicNav.learning.notes'), description: t('routes.root.publicNav.learning.notesDescription'), href: '/ai-demo?tab=demo' },
        { icon: Sparkles, title: t('routes.root.publicNav.learning.focus'), description: t('routes.root.publicNav.learning.focusDescription'), href: '/feature/student' },
        { icon: UsersRound, title: t('routes.root.publicNav.learning.classroom'), description: t('routes.root.publicNav.learning.classroomDescription'), href: '/feature/student' },
      ],
      featured: {
        eyebrow: t('routes.root.publicNav.learning.featuredEyebrow'),
        title: t('routes.root.publicNav.learning.featuredTitle'),
        description: t('routes.root.publicNav.learning.featuredDescription'),
        href: '/ai-demo?tab=chat',
      },
    },
    {
      id: 'roles',
      label: t('routes.root.publicNav.roles.label'),
      description: t('routes.root.publicNav.roles.description'),
      items: [
        { icon: BarChart3, title: t('routes.root.publicNav.roles.leaders'), description: t('routes.root.publicNav.roles.leadersDescription'), href: '/feature/admin' },
        { icon: BookOpenCheck, title: t('routes.root.publicNav.roles.teachers'), description: t('routes.root.publicNav.roles.teachersDescription'), href: '/feature/teacher' },
        { icon: GraduationCap, title: t('routes.root.publicNav.roles.students'), description: t('routes.root.publicNav.roles.studentsDescription'), href: '/feature/student' },
        { icon: HeartHandshake, title: t('routes.root.publicNav.roles.families'), description: t('routes.root.publicNav.roles.familiesDescription'), href: '/feature/parent' },
      ],
      featured: {
        eyebrow: t('routes.root.publicNav.roles.featuredEyebrow'),
        title: t('routes.root.publicNav.roles.featuredTitle'),
        description: t('routes.root.publicNav.roles.featuredDescription'),
        href: '/features',
      },
    },
    {
      id: 'resources',
      label: t('routes.root.publicNav.resources.label'),
      description: t('routes.root.publicNav.resources.description'),
      items: [
        { icon: PlayCircle, title: t('routes.root.publicNav.resources.demo'), description: t('routes.root.publicNav.resources.demoDescription'), href: '/demo' },
        { icon: LibraryBig, title: t('routes.root.publicNav.resources.tutorials'), description: t('routes.root.publicNav.resources.tutorialsDescription'), href: '/tutoriales' },
        { icon: CircleHelp, title: t('routes.root.publicNav.resources.support'), description: t('routes.root.publicNav.resources.supportDescription'), href: '/contact' },
        { icon: Building2, title: t('routes.root.publicNav.resources.about'), description: t('routes.root.publicNav.resources.aboutDescription'), href: '/about' },
      ],
      featured: {
        eyebrow: t('routes.root.publicNav.resources.featuredEyebrow'),
        title: t('routes.root.publicNav.resources.featuredTitle'),
        description: t('routes.root.publicNav.resources.featuredDescription'),
        href: '/contact',
      },
    },
  ]

  const cancelMegaMenuClose = () => {
    if (megaMenuCloseTimeout.current) {
      clearTimeout(megaMenuCloseTimeout.current)
      megaMenuCloseTimeout.current = null
    }
  }

  const openMegaMenu = (menuId: string) => {
    cancelMegaMenuClose()
    setActiveMegaMenu(menuId)
  }

  const scheduleMegaMenuClose = () => {
    cancelMegaMenuClose()
    megaMenuCloseTimeout.current = setTimeout(() => {
      setActiveMegaMenu(null)
      megaMenuCloseTimeout.current = null
    }, 220)
  }

  const DesktopNavigation = () => (
    <div
      className="flex items-center"
      onMouseEnter={cancelMegaMenuClose}
      onMouseLeave={scheduleMegaMenuClose}
    >
      {publicNavGroups.map((group) => (
        <div key={group.id} onMouseEnter={() => openMegaMenu(group.id)}>
          <button
            type="button"
            aria-expanded={activeMegaMenu === group.id}
            className={`flex items-center gap-1 rounded-full px-3 py-2 text-sm font-bold transition ${
              activeMegaMenu === group.id
                ? 'bg-blue-50 text-[#3156bd]'
                : 'text-[#243766] hover:bg-blue-50 hover:text-[#3156bd]'
            }`}
            onFocus={() => openMegaMenu(group.id)}
            onClick={() => {
              cancelMegaMenuClose()
              setActiveMegaMenu(activeMegaMenu === group.id ? null : group.id)
            }}
          >
            {group.label}
            <ChevronDown className={`h-4 w-4 transition ${activeMegaMenu === group.id ? 'rotate-180' : ''}`} />
          </button>

          {activeMegaMenu === group.id && (
            <div
              className="fixed left-1/2 top-[101px] w-[min(940px,calc(100vw-48px))] -translate-x-1/2 overflow-hidden rounded-[1.75rem] border border-blue-100 bg-white shadow-[0_30px_80px_rgba(12,34,91,0.22)]"
              onMouseEnter={cancelMegaMenuClose}
              onMouseLeave={scheduleMegaMenuClose}
            >
              <div className="grid grid-cols-[1fr_280px]">
                <div className="p-7">
                  <div className="mb-5">
                    <p className="text-xs font-black uppercase tracking-[0.18em] text-[#4964ae]">{group.label}</p>
                    <p className="mt-2 text-sm text-slate-500">{group.description}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {group.items.map((item) => {
                      const Icon = item.icon
                      return (
                        <a
                          key={item.title}
                          href={item.href}
                          onClick={() => setActiveMegaMenu(null)}
                          className="group/item flex gap-3 rounded-2xl p-3 transition hover:bg-[#f2f6ff]"
                        >
                          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#eaf0ff] text-[#3156bd] transition group-hover/item:bg-[#3156bd] group-hover/item:text-white">
                            <Icon className="h-5 w-5" />
                          </span>
                          <span>
                            <span className="block text-sm font-black text-[#14275e]">{item.title}</span>
                            <span className="mt-1 block text-xs leading-5 text-slate-500">{item.description}</span>
                          </span>
                        </a>
                      )
                    })}
                  </div>
                </div>
                <a
                  href={group.featured.href}
                  onClick={() => setActiveMegaMenu(null)}
                  className="relative flex flex-col justify-end overflow-hidden bg-[#102a72] p-7 text-white"
                >
                  <div className="absolute -right-12 -top-12 h-40 w-40 rounded-full border-[28px] border-white/10" />
                  <div className="absolute -bottom-10 -left-10 h-32 w-32 rounded-full bg-[#f7c74f]/15" />
                  <span className="relative text-xs font-black uppercase tracking-[0.18em] text-[#f7c74f]">{group.featured.eyebrow}</span>
                  <span className="relative mt-3 text-2xl font-black leading-tight tracking-[-0.03em]">{group.featured.title}</span>
                  <span className="relative mt-3 text-sm leading-6 text-blue-100">{group.featured.description}</span>
                  <span className="relative mt-6 inline-flex items-center gap-2 text-sm font-black">
                    {t('routes.root.publicNav.explore')}
                    <ArrowRight className="h-4 w-4" />
                  </span>
                </a>
              </div>
            </div>
          )}
        </div>
      ))}
      <Link to="/pricing" className="rounded-full px-3 py-2 text-sm font-bold text-[#243766] transition hover:bg-blue-50 hover:text-[#3156bd]">
        {t('routes.root.navigation.pricing')}
      </Link>
    </div>
  )

  const MobileNavigation = () => (
    <div className="max-h-[calc(100vh-76px)] overflow-y-auto">
      <div className="flex items-center justify-between rounded-2xl bg-[#071a50] px-4 py-3 text-xs font-bold text-white">
        <a href="/contact" onClick={() => setIsMobileMenuOpen(false)}>{t('routes.root.publicNav.utility.support')}</a>
        <button type="button" onClick={() => i18n.changeLanguage((i18n.language || '').startsWith('fr') ? 'en' : 'fr')}>
          {(i18n.language || '').startsWith('fr') ? 'EN' : 'FR'}
        </button>
      </div>
      <div className="mt-3 space-y-2">
        {publicNavGroups.map((group) => (
          <details key={group.id} className="group rounded-2xl border border-blue-100 bg-white">
            <summary className="flex cursor-pointer list-none items-center justify-between px-4 py-4 font-black text-[#14275e]">
              {group.label}
              <ChevronDown className="h-5 w-5 transition group-open:rotate-180" />
            </summary>
            <div className="grid gap-1 border-t border-blue-50 p-2 sm:grid-cols-2">
              {group.items.map((item) => {
                const Icon = item.icon
                return (
                  <a key={item.title} href={item.href} onClick={() => setIsMobileMenuOpen(false)} className="flex items-center gap-3 rounded-xl p-3 hover:bg-blue-50">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#eaf0ff] text-[#3156bd]"><Icon className="h-4 w-4" /></span>
                    <span className="text-sm font-bold text-[#243766]">{item.title}</span>
                  </a>
                )
              })}
            </div>
          </details>
        ))}
      </div>
      <Link to="/pricing" onClick={() => setIsMobileMenuOpen(false)} className="mt-2 block rounded-2xl border border-blue-100 px-4 py-4 font-black text-[#14275e]">
        {t('routes.root.navigation.pricing')}
      </Link>
      <div className="mt-4 grid grid-cols-2 gap-2"><AuthButtons /></div>
    </div>
  )

  const AuthButtons = () => (
    <>
      {isLoggedIn ? (
        <div className="flex items-center space-x-4">
          {(userRole === 'Student' || userRole === 'Teacher') && (
            <MessageIcon className="mr-2" />
          )}
          <UserProfileMenu onLogout={handleLogout} />
        </div>
      ) : (
        <>
          <Link
            to="/login"
            className="rounded-full px-4 py-2 text-sm font-extrabold text-[#173783] transition hover:bg-blue-50"
          >
            {t('routes.root.auth.login')}
          </Link>
          <Link
            to="/demo"
            className="rounded-full bg-[#f7c74f] px-5 py-3 text-sm font-extrabold text-[#10204f] shadow-[0_10px_24px_rgba(59,70,111,0.14)] transition hover:-translate-y-0.5 hover:bg-[#ffd76d]"
          >
            {t('routes.root.auth.getDemo')}
          </Link>
        </>
      )}
    </>
  )

  

  return (
    <QueryClientProvider client={queryClient}>
      <SEOProvider>
        <div className="flex flex-col min-h-screen">
          <header className="sticky top-0 z-40 border-b border-blue-100/80 bg-white/95 shadow-[0_8px_30px_rgba(30,56,120,0.08)] backdrop-blur-xl">
            <div className="hidden bg-[#071a50] text-white lg:block">
              <div className="mx-auto flex h-9 max-w-7xl items-center justify-between px-10 text-xs font-bold">
                <span className="text-blue-100">{t('routes.root.publicNav.utility.message')}</span>
                <div className="flex items-center gap-5">
                  <Link to="/feature/parent" className="text-blue-100 transition hover:text-white">{t('routes.root.publicNav.utility.families')}</Link>
                  <Link to="/tutoriales" className="text-blue-100 transition hover:text-white">{t('routes.root.publicNav.utility.tutorials')}</Link>
                  <Link to="/contact" className="text-blue-100 transition hover:text-white">{t('routes.root.publicNav.utility.support')}</Link>
                  <button type="button" className="rounded-full border border-white/20 px-2.5 py-1 transition hover:bg-white hover:text-[#071a50]" onClick={() => i18n.changeLanguage((i18n.language || '').startsWith('fr') ? 'en' : 'fr')}>
                    {(i18n.language || '').startsWith('fr') ? 'EN' : 'FR'}
                  </button>
                </div>
              </div>
            </div>
            <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3 sm:px-8 lg:px-10">
              <div className="font-poppins text-[22px] font-bold text-gray-800">
                <Link
                  to={isLoggedIn ? getDashboardPath() : '/'}
                  className="flex items-center gap-2"
                >
                  <img
                    src="/brand/jeridschool-logo-full.png"
                    alt="JeridSchool"
                    className="h-8 w-auto max-w-[220px] object-contain sm:h-9"
                  />
                </Link>
              </div>

              <nav className="hidden items-center lg:flex">
                <DesktopNavigation />
              </nav>

              <div className="hidden items-center gap-1 lg:flex">
                <AuthButtons />
              </div>

              <button
                className="flex h-11 w-11 items-center justify-center rounded-full bg-blue-50 text-[#173783] focus:outline-none focus:ring-2 focus:ring-[#3156bd]/30 lg:hidden"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                aria-label={isMobileMenuOpen ? 'Close navigation' : 'Open navigation'}
              >
                {isMobileMenuOpen ? (
                  <X className="h-6 w-6" />
                ) : (
                  <Menu className="h-6 w-6" />
                )}
              </button>
            </div>

            {isMobileMenuOpen && (
              <div className="border-t border-blue-100 bg-white lg:hidden">
                <nav className="mx-auto flex max-w-7xl flex-col gap-1 px-5 py-5 sm:px-8">
                  <MobileNavigation />
                </nav>
              </div>
            )}
          </header>
          <main className="flex-grow">
            <div className="flex-1 overflow-x-hidden overflow-y-auto">
              <Outlet />
            </div>
          </main>
          <Footer />
        </div>
        <Toaster position="top-right" richColors />
        <FloatingWhatsAppButton />
      </SEOProvider>
    </QueryClientProvider>
  )
}

function FloatingWhatsAppButton() {
  return (
    <a
      href="https://wa.me/21623353869"
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Contact JeridSchool on WhatsApp"
      className="fixed bottom-6 right-6 z-50 flex h-16 w-16 items-center justify-center rounded-full bg-[#25D366] text-white shadow-2xl shadow-green-500/30 transition-all duration-300 hover:-translate-y-1 hover:scale-105 hover:bg-[#1ebe5d] focus:outline-none focus:ring-4 focus:ring-[#25D366]/30"
    >
      <svg className="h-8 w-8" viewBox="0 0 32 32" fill="currentColor" aria-hidden="true">
        <path d="M16.04 3C8.86 3 3.03 8.83 3.03 16c0 2.3.6 4.54 1.75 6.52L3 29l6.64-1.74A12.92 12.92 0 0 0 16.04 29c7.17 0 13-5.83 13-13s-5.83-13-13-13Zm0 23.8c-2.04 0-4.03-.57-5.76-1.65l-.41-.25-3.94 1.03 1.05-3.84-.27-.43A10.73 10.73 0 0 1 5.23 16c0-5.96 4.85-10.8 10.81-10.8S26.84 10.04 26.84 16s-4.85 10.8-10.8 10.8Zm5.92-8.08c-.32-.16-1.9-.94-2.2-1.05-.3-.11-.52-.16-.74.16-.22.32-.85 1.05-1.04 1.27-.19.22-.38.24-.7.08-.32-.16-1.36-.5-2.59-1.6-.96-.85-1.6-1.91-1.79-2.23-.19-.32-.02-.5.14-.66.14-.14.32-.38.48-.57.16-.19.22-.32.32-.54.11-.22.05-.41-.03-.57-.08-.16-.74-1.79-1.01-2.45-.27-.64-.54-.55-.74-.56h-.63c-.22 0-.57.08-.87.41-.3.32-1.14 1.11-1.14 2.71s1.17 3.15 1.33 3.36c.16.22 2.3 3.51 5.57 4.92.78.34 1.39.54 1.86.69.78.25 1.49.21 2.05.13.63-.09 1.9-.78 2.17-1.52.27-.75.27-1.38.19-1.52-.08-.14-.3-.22-.62-.38Z" />
      </svg>
    </a>
  )
}

function SubdomainSchoolComponent() {
  const { t } = useTranslation()
  const schoolName = schoolService.getCurrentSchoolFromSubdomain()

  const {
    data: school,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['school', schoolName],
    queryFn: () =>
      schoolName
        ? schoolService.getSchoolByUrl(schoolName)
        : Promise.reject('No school name found'),
    enabled: !!schoolName,
    staleTime: 1000 * 60 * 10,
    gcTime: 1000 * 60 * 30,
    refetchOnWindowFocus: false,
    retry: 1,
  })

  if (!schoolName) {
    return (
      <QueryClientProvider client={queryClient}>
        <div className="container mx-auto px-4 py-8">
          <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 rounded-lg p-4">
            <h2 className="text-xl font-bold mb-2">
              {t('routes.root.subdomain.notFound.title')}
            </h2>
            <p>{t('routes.root.subdomain.notFound.message')}</p>
          </div>
          <SubdomainDebugger />
        </div>
        <Toaster position="top-right" richColors />
      </QueryClientProvider>
    )
  }

  if (isLoading) {
    return (
      <QueryClientProvider client={queryClient}>
        <div className="flex justify-center items-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
          <span className="ml-4">{t('routes.root.subdomain.loading')}</span>
        </div>
        <Toaster position="top-right" richColors />
      </QueryClientProvider>
    )
  }

  if (error || !school) {
    return (
      <QueryClientProvider client={queryClient}>
        <div className="container mx-auto px-4 py-8">
          <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4">
            <h2 className="text-xl font-bold mb-2">
              {t('routes.root.subdomain.error.title')}
            </h2>
            <p>{t('routes.root.subdomain.error.message')}</p>
            <p className="mt-2 text-sm text-red-600">
              {error instanceof Error
                ? error.message
                : 'Unknown error occurred'}
            </p>
            <div className="mt-4 p-3 bg-gray-100 rounded text-sm font-mono">
              <p>{t('routes.root.subdomain.error.debugInfo.title')}</p>
              <p>
                {t('routes.root.subdomain.error.debugInfo.requestedSchool')}{' '}
                {schoolName}
              </p>
              <p>
                {t('routes.root.subdomain.error.debugInfo.subdomainAccess')} Yes
              </p>
            </div>
          </div>
          <SubdomainDebugger />
        </div>
        <Toaster position="top-right" richColors />
      </QueryClientProvider>
    )
  }

  return (
    <QueryClientProvider client={queryClient}>
      <SEOProvider>
        <SchoolLandingPage school={school} />
        <Toaster position="top-right" richColors />
        <FloatingWhatsAppButton />
      </SEOProvider>
    </QueryClientProvider>
  )
}
